/*
 * 戒导航 · 上传模块（jiedh/upload）—— [upl-image-preview] BBCode 的词法与序列化工具
 * =============================================================================
 * 设计依据：doc/输入框富文本编辑器设计方案.md §8.5（方案 B）
 *
 * fof/upload 的 image-preview 模板是一种「自闭合 BBCode」（单行、无闭合标签）：
 *   [upl-image-preview uuid=<uuid> url=<url> alt=<alt> thumbnail_url=<url>]
 * 四个属性的权威顺序与取值来源，见
 *   vendor/fof/upload/src/Templates/ImagePreviewTemplate::preview()
 *   vendor/fof/upload/src/Repositories/FileRepository::getBasename()
 *   · uuid          ← $file->uuid
 *   · url           ← $file->url
 *   · alt           ← $file->base_name（slug + 扩展名，正常不含空格，也不含 `]`）
 *   · thumbnail_url ← $file->thumbnail_url ?: $file->url（缺省回退 url）
 *
 * 本文件是纯字符串处理（**不引用 tiptap / markdown-it / flarum**），供三处接入点复用：
 *   · 腿 B tokenizer ：把 BBCode 切成一个 upl_image_preview 内联 token；
 *   · 腿 B getAttrs  ：把 token 还原成 Node 属性；
 *   · 腿 C 序列化器  ：把 Node 属性**原样**还原成 BBCode。
 *
 * ⚠️ 红线：序列化结果必须与改造前完全一致（同标签名、同属性名、同顺序），
 * 这样 packages/content-guard 的外链拦截规则第 ⑤ 条（upl-image-preview 的
 * url / thumbnail_url 属性）才能继续命中，详见设计文档 §8.6。
 */

/** BBCode 标签名（对应 vendor/fof/upload/src/Templates/ImagePreviewTemplate 的 $tag）。 */
export const UPL_IMAGE_PREVIEW_TAG = 'upl-image-preview';

/** markdown-it token 类型名（tokenizer 产出、buildTokens 消费，两侧必须完全一致）。 */
export const UPL_IMAGE_PREVIEW_TOKEN = 'upl_image_preview';

/**
 * ProseMirror / Tiptap 节点名。
 * 必须与 buildTokens 里的 `node:` 值、buildNodes 里序列化器的 key、Tiptap Node 的 name 三处一致。
 */
export const UPL_IMAGE_PREVIEW_NODE = 'uplImagePreview';

/** 自研 Tiptap Node 的属性集合（Node 属性名 → 语义）。 */

/* -------------------------------------------------------------------------- */
/* 字符码常量（与 vendor/fof/rich-text 的 markdown-it 插件保持同一写法，便于对照） */
/* -------------------------------------------------------------------------- */
const CODE_BRACKET_OPEN = 0x5b; // [
const CODE_BRACKET_CLOSE = 0x5d; // ]
const CODE_SPACE = 0x20; // 空格
const CODE_TAB = 0x09; // 制表符
const CODE_LF = 0x0a; // 换行
const CODE_DOUBLE_QUOTE = 0x22; // "
const CODE_SINGLE_QUOTE = 0x27; // '

/** `[upl-image-preview` 前缀（不含后续空白与 `]`）。 */
const OPEN_PREFIX = `[${UPL_IMAGE_PREVIEW_TAG}`;

/**
 * 从 `from` 起寻找与 `[upl-image-preview` 配对的 `]`。
 *   · 支持 "…" / '…' 引号包裹的属性值：跳过引号内部的 `]`；
 *   · 遇到换行立即放弃（该 BBCode 永远是单行，跨行一定不是它）。
 *
 * @returns 结束 `]` 的下标；未找到返回 -1。
 */
function findClosingBracket(src, from, max) {
  let quote = 0;
  for (let i = from; i < max; i++) {
    const code = src.charCodeAt(i);
    if (quote) {
      if (code === quote) quote = 0; // 命中成对引号，退出引号态
      continue;
    }
    if (code === CODE_DOUBLE_QUOTE || code === CODE_SINGLE_QUOTE) {
      quote = code;
      continue;
    }
    if (code === CODE_BRACKET_CLOSE) return i;
    if (code === CODE_LF) return -1;
  }
  return -1;
}

/**
 * 属性提取正则：key=value，value 支持 "…" / '…' / 无引号（读到空白或 `]` 为止）。
 * 与 packages/content-guard/src/ExternalLinkPolicy.php 的 attributeOf() 口径保持一致，
 * 保证「编辑器认得的写法」与「拦截器认得的写法」不出现偏差。
 */
const ATTRIBUTE_PATTERN = /([a-zA-Z_][\w-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s\]]+))/g;

/**
 * 把 `[upl-image-preview` 与 `]` 之间的原文解析成属性字典。
 * 键统一小写，便于取 `thumbnail_url` 这类下划线属性。
 */
export function parseUplImagePreviewAttributes(inner) {
  const attributes = {};
  ATTRIBUTE_PATTERN.lastIndex = 0;
  let match;
  while ((match = ATTRIBUTE_PATTERN.exec(inner)) !== null) {
    // match[2] / match[3] / match[4] 依次是双引号、单引号、无引号三种取值
    attributes[match[1].toLowerCase()] = match[2] ?? match[3] ?? match[4] ?? '';
  }
  return attributes;
}

/**
 * 由 BBCode 属性字典生成 Node 属性。
 * thumbnail_url 缺省时回退 url —— 与 ImagePreviewTemplate::preview() 的行为完全一致。
 */
export function toNodeAttrs(attributes) {
  const url = attributes.url || null;
  return {
    uuid: attributes.uuid || null,
    url,
    alt: attributes.alt || null,
    thumbnailUrl: attributes.thumbnail_url || url
  };
}

/**
 * 属性值中若出现空白 / `]` / 引号，用双引号包裹，避免破坏 BBCode 结构。
 * 正常数据（R2 图片地址 + slug 文件名）不会触发包裹，走的是与 preview() 完全一致的无引号写法。
 */
function quoteIfNeeded(value) {
  return /[\s\]"']/.test(value) ? `"${value.replace(/"/g, '')}"` : value;
}

/**
 * 把 Node 属性**原样**还原成 fof/upload 认得的自闭合 BBCode。
 * 属性顺序固定为 uuid → url → alt → thumbnail_url，与 ImagePreviewTemplate::preview() 一致。
 */
export function formatUplImagePreview(attrs) {
  const url = attrs.url ?? '';
  const thumbnailUrl = attrs.thumbnailUrl || url;
  return `[${UPL_IMAGE_PREVIEW_TAG}` + ` uuid=${quoteIfNeeded(attrs.uuid ?? '')}` + ` url=${quoteIfNeeded(url)}` + ` alt=${quoteIfNeeded(attrs.alt ?? '')}` + ` thumbnail_url=${quoteIfNeeded(thumbnailUrl)}]`;
}

/**
 * markdown-it 内联规则：在当前位置尝试匹配一整条 `[upl-image-preview …]`。
 *
 * 契约（与 vendor/fof/rich-text/…/markdown-it/altText.ts 完全一致，必须遵守）：
 *   · 返回 true 时**必须**已推进 state.pos，否则 ParserInline 会抛
 *     「inline rule didn't increment state.pos」；
 *   · silent 模式（markdown-it 的 lookahead / skipToken）只推进位置、不产出 token。
 */
export function tokenizeUplImagePreview(state, silent) {
  const src = state.src;
  const start = state.pos;
  const max = state.posMax;

  // 必须以 '[' 开头，且紧跟完整标签名
  if (src.charCodeAt(start) !== CODE_BRACKET_OPEN) return false;
  if (src.slice(start, start + OPEN_PREFIX.length) !== OPEN_PREFIX) return false;

  // 标签名之后必须是空白或 ']'，否则 [upl-image-preview-xxx] 之类的普通文本会被误吞
  const afterTag = src.charCodeAt(start + OPEN_PREFIX.length);
  if (afterTag !== CODE_SPACE && afterTag !== CODE_TAB && afterTag !== CODE_LF && afterTag !== CODE_BRACKET_CLOSE) {
    return false;
  }
  const end = findClosingBracket(src, start + OPEN_PREFIX.length, max);
  if (end === -1) return false; // 没有配对 ']'，交还给其它规则 / 文本规则处理

  if (!silent) {
    // 产出 node 型 token（nesting = 0）；tag 传空串：该 token 只用于喂给 MarkdownParser，
    // 不参与 markdown-it 自身的 HTML 输出（tokenizerParams 里 html 也是关闭的）。
    const token = state.push(UPL_IMAGE_PREVIEW_TOKEN, '', 0);
    token.content = src.slice(start + OPEN_PREFIX.length, end);
  }
  state.pos = end + 1;
  return true;
}